import { InputField } from '@atoms/input';
import BlockBase from '@components/dnd/base/blockBase';
import { Column } from '@components/dnd/base/blockBase/body.block.style';
import { InteractionZone } from '@components/dnd/base/interaction';
import StateMenu from '@components/stateMenu';
import { useAtom } from '@hooks/useAtom';
import useProcess from '@hooks/useProcess';
import useStores from '@hooks/useStore';
import EndControlsFB from '@library/formBuilder/endControls';
import { isTIBlocks } from '@library/traceInfo';
import { Checkbox, FormLabel, Modal, ModalClose } from '@mui/joy';
import type {
  AtomReference,
  DNDBlock,
  TIBlocks
} from '@stratumn/studio-shared';
import { AppWindow, type LucideIcon } from 'lucide-react';
import { observer } from 'mobx-react';
import { type ChangeEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ParamsList } from '@/routes/routes.types';
import { newError } from '@/services/errors/errors';

import {
  AddWidgetButton,
  LabeledVariable,
  TOWidgetMessage
} from '../blocks.style';
import { initialTIWindowData } from './window.data';

export const WindowIcon: LucideIcon = AppWindow;

const Window = (block: DNDBlock) => {
  const workflowId = useParams()[ParamsList.WorkflowId] as string;
  const [openModal, setOpenModal] = useState<boolean>(false);

  const { dndStore, atomStore } = useStores();

  const dataItem = useAtom({
    type: 'TI_Window',
    sourceId: block.id,
    parentId: workflowId,
    parentKind: 'traceInfo',
    initialData: initialTIWindowData
  });

  const process = useProcess();

  if (!dataItem) {
    return <></>;
  }

  if (!process) {
    return <></>;
  }

  const traceInfoDnd = dndStore.get(process.workflows[0]?.traceInfoDndId);
  if (!traceInfoDnd) {
    return <></>;
  }

  const dndPath = `${block.path}:window`;

  const onVariableSelected = (selectedVariableRef: Maybe<AtomReference>) => {
    if (!selectedVariableRef) {
      return;
    }

    const selectedVariable = atomStore.getAtomById(
      selectedVariableRef.dataItemId,
      selectedVariableRef.blockType as TIBlocks
    );

    if (!selectedVariable || selectedVariable instanceof Error) {
      throw newError('WIN-1234', 'Variable not found');
    }

    if (!selectedVariable.variableInfo) {
      throw newError('WIN-4321', 'Selected variable has no variable infos');
    }

    const TIBlockType = `TI_${selectedVariable.variableInfo.resolvedType}`;

    if (!isTIBlocks(TIBlockType)) {
      toast.info('Cannot select this type of variable yet.');
      throw newError(
        'WIN-z123jo',
        'Selected variable resolved type is not a TI block'
      );
    }

    const newTIBlock = traceInfoDnd.createDndBlock(TIBlockType, '');

    newTIBlock.other.selectedVariableRef = selectedVariableRef;

    // The new TI block is added to the window
    const stateWithNewTIBlock = traceInfoDnd.insertBlockInState(
      dndPath,
      traceInfoDnd.state,
      newTIBlock
    );

    traceInfoDnd.setState(stateWithNewTIBlock);

    setTimeout(() => {
      const newTIBlockAtom = atomStore.get(newTIBlock.atomId);

      if (!newTIBlockAtom) {
        throw newError('WIN-0NmZL', 'New TI block atom not found');
      }

      newTIBlockAtom.addReferenceToAtom(selectedVariable.id);
      newTIBlockAtom.addReferenceToAtom(dataItem.id);
    }, 500);
  };

  const onTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    dataItem.data.title = event.target.value;
  };

  return (
    <>
      <BlockBase
        dataItem={dataItem}
        dndBlock={block}
        endControls={
          <EndControlsFB
            dataItem={dataItem}
            dndBlock={block}
            hasRequired={false}
          />
        }
        icon={WindowIcon}
        title={dataItem.data.title}
      >
        <Column $width="200px">
          <InputField
            label="Window title"
            onChange={onTitleChange}
            placeholder="Aa"
            value={dataItem.data.title}
          />
        </Column>
        <Column>
          <Column $width="200px">
            <LabeledVariable>
              <FormLabel>Configuration</FormLabel>
              <Checkbox
                checked={dataItem.data.isCollapsable === true}
                color="neutral"
                label="Collapsable window"
                onChange={() => {
                  dataItem.data.isCollapsable = !dataItem.data.isCollapsable;
                }}
                size="sm"
                variant="outlined"
              />
            </LabeledVariable>
          </Column>
        </Column>
        <div data-tag="widget-list" style={{ width: '100%' }}>
          <Column>
            <FormLabel sx={{ fontSize: '12px' }}>Widgets</FormLabel>
            <AddWidgetButton
              onClick={() => setOpenModal(true)}
              size="sm"
              value="Add a widget"
            />
          </Column>

          {block.props?.window?.length ? (
            <InteractionZone
              dndBlock={block.props?.window ?? []}
              path={dndPath}
            />
          ) : (
            <TOWidgetMessage>
              No widgets. Click on the above button to add a widget!
            </TOWidgetMessage>
          )}
        </div>
      </BlockBase>

      <Modal
        aria-describedby="modal-desc"
        aria-labelledby="modal-title"
        onClose={() => setOpenModal(false)}
        open={openModal}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: '70px'
        }}
      >
        <ModalClose sx={{ m: 1 }} variant="plain" />
        <StateMenu
          onClose={() => setOpenModal(false)}
          onSelected={onVariableSelected}
        />
      </Modal>
    </>
  );
};

export default observer(Window);
